<template>
  <div>
    <div class="md:border-b md:mb-15">
      <TheHeader
        with-menu
        back-route="."
      />
    </div>
    <BaseSection
      v-if="opportunity"
      class="mb-9 opportunity-details md:hidden"
    >
      <BaseTitleGroup
        :title="opportunity.data.attributes.name"
        :subtitle="$t('New opportunities')"
        class="mb-2"
      />
      <BaseParagraph class="mb-6">
        {{ attributes.previewText }}
      </BaseParagraph>
      <OpportunityWarningBox :attributes="attributes" />
      <BaseProgressBar
        v-if="!attributes.wishesDisabled"
        :current="attributes.wishesAmount"
        :goal="attributes.targetAmount"
        :over-subscription="attributes.overSubscription"
        class="mb-4"
      />
      <BaseButton
        :to="`/opportunities/${$route.params.id}/summary`"
        :title="$t('Summary')"
      />
    </BaseSection>
    <BaseSection class="opportunity-links md:hidden">
      <BaseSectionHeader class="border-t pt-4 border-strong-shadow-color mb-4">
        {{ $t('Timeline') }}
      </BaseSectionHeader>
      <BaseTimeline
        :timeline="timeline"
        class="pl-2 mb-8"
      />
      <BaseSectionHeader class="border-t pt-4 border-strong-shadow-color">
        {{ $t('Opportunity details') }}
      </BaseSectionHeader>
      <BaseMenuList
        :items="menuItems"
        class="mb-8"
      />
      <InvestButton
        :can-invest="canInvest"
        :can-decline="canDecline"
        :disabled="attributes.wishesDisabled"
      />
      <AcceptButton
        :can-accept="canAccept"
      />
    </BaseSection>
    <ColLayout
      v-if="opportunity"
      :title="opportunity.data.attributes.name"
      :subtitle="$t('New opportunities')"
      :preview-text="attributes.previewText"
      class="opportunity-desktop hidden md:visible"
    >
      <tab
        id="summary"
        :name="$t('Summary')"
      >
        <BaseParagraph
          class="mb-6"
          v-html="opportunity.data.attributes.summary"
        />
        <BaseParagraph
          class="italic text-xs"
          v-html="$t('Opportunity risks')"
        />
      </tab>
      <tab
        id="Opportunity operation details"
        :name="$t('Operation details')"
      >
        <BaseParagraph
          v-html="opportunity.data.attributes.operationDetails"
        />
      </tab>
      <tab
        id="Kerala Analysis"
        :name="$t('Kerala Analysis')"
      >
        <BaseParagraph class="mb-8">
          {{ $t('Kerala Analysis description') }}
        </BaseParagraph>
        <iframe
          v-if="opportunity.data.attributes.keralaAnalysisDocumentId"
          :src="`https://drive.google.com/file/d/${opportunity.data.attributes.keralaAnalysisDocumentId}/preview`"
          class="w-full mb-8 iframe-custom-height"
        />
      </tab>
      <tab
        id="Management report"
        :name="$t('Management report')"
      >
        <template v-if="opportunity.data.attributes.managementReportDocumentId">
          <BaseParagraph class="mb-8">
            {{ $t('Management report hint') }}
          </BaseParagraph>
          <iframe
            :src="`https://drive.google.com/file/d/${opportunity.data.attributes.managementReportDocumentId}/preview`"
            class="w-full mb-8 iframe-custom-height"
          />
        </template>
        <BaseParagraph v-else>
          {{ $t('No Management report hint') }}
        </BaseParagraph>
      </tab>
      <template slot="right">
        <OpportunityWarningBox
          :attributes="attributes"
          with-shadow
        />
        <BaseProgressBar
          v-if="!attributes.wishesDisabled"
          :current="attributes.wishesAmount"
          :goal="attributes.targetAmount"
          :over-subscription="attributes.overSubscription"
          class="mb-4"
        />
        <OpportunityTimeline :timeline="timeline" />
        <InvestButton
          :can-invest="canInvest"
          :can-decline="canDecline"
          :disabled="attributes.wishesDisabled"
        />
        <AcceptButton
          :can-accept="canAccept"
        />
      </template>
    </ColLayout>
  </div>
</template>

<script>
import { get } from 'lodash';
import loadOpportunity from '../mixins/loadOpportunity';
import TheHeader from '../components/TheHeader.vue';
import BaseSection from '../components/BaseSection.vue';
import BaseTitleGroup from '../components/BaseTitleGroup.vue';
import BaseParagraph from '../components/BaseParagraph.vue';
import BaseProgressBar from '../components/BaseProgressBar.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseSectionHeader from '../components/BaseSectionHeader.vue';
import BaseTimeline from '../components/BaseTimeline.vue';
import BaseMenuList from '../components/BaseMenuList.vue';
import BaseOrangeText from '../components/BaseOrangeText.vue';
import ColLayout from './partials/ColLayout.vue';
import InvestButton from './partials/InvestButton.vue';
import AcceptButton from './partials/AcceptButton.vue';
import OpportunityTimeline from './partials/OpportunityTimeline.vue';
import OpportunityWarningBox from './partials/OpportunityWarningBox.vue';

export default {
  name: 'OpportunityShow',
  components: {
    TheHeader,
    BaseSection,
    BaseTitleGroup,
    BaseParagraph,
    BaseProgressBar,
    BaseButton,
    BaseSectionHeader,
    BaseTimeline,
    BaseMenuList,
    BaseOrangeText,
    ColLayout,
    InvestButton,
    AcceptButton,
    OpportunityTimeline,
    OpportunityWarningBox,
  },
  mixins: [loadOpportunity],
  data() {
    const baseUrl = `/opportunities/${this.$route.params.id}/`;
    return {
      menuItems: [
        {
          title: 'Résumé',
          routeName: `${baseUrl}summary`,
        }, {
          title: 'Opération',
          routeName: `${baseUrl}operation-details`,
        }, {
          title: 'Analyse de Kerala',
          routeName: `${baseUrl}kerala-analysis`,
        }, {
          title: 'Deck du management',
          routeName: `${baseUrl}management-report`,
        },
      ],
    };
  },
  computed: {
    timeline() {
      const defaultTimeline = [
        [null, 'Opportunité publiée'],
        ['Recueil des souhaits', 'Montant atteint'],
        ['Phase administrative', 'Phase administrative fermée'],
        ['Investissement en cours'],
        ['Investissement réalisé'],
      ];
      return {
        items: defaultTimeline,
        activeIndex: this.timelineIndex,
      };
    },
    timelineIndex() {
      if (this.opportunity) {
        const { attributes } = this.opportunity.data;
        const { status } = attributes;
        if (attributes.wishesDisabled) {
          return 0.5;
        }
        if (status === 'published') {
          return 1;
        } else if (status === 'closed') {
          return 1.5;
        } else if (status === 'entered_administrative_phase') {
          return 2;
        } else if (status === 'closed_administrative_phase') {
          return 3;
        }
      }
      return 0;
    },
    attributes() {
      return get(this.opportunity, 'data.attributes', {});
    },
  },
};
</script>

<style lang="css" scoped>
.iframe-custom-height {
  height: 45vh;
}
</style>
